/**
 * Reviews showcase (compact).
 *
 * Tiles are dealt round-robin into rows (two on wider screens, one on phones). Each row is
 * cloned until it can loop, then drifts with requestAnimationFrame; the offset wraps by exactly
 * one set width, so the loop has no visible seam. Rows move in opposite directions.
 *
 * - Scroll: page scroll velocity pushes the rows (and the ticker) along, reversing them when
 *   scrolling up, then eases back to the cruising speed.
 * - Drag: rows can be grabbed with mouse or finger and flung; the fling decays with inertia.
 * - Hover (desktop): the row under the pointer eases to a stop, the other slows; the card
 *   tilts towards the pointer.
 * - Phones (single row): the card in the middle of the screen is in focus, cards towards the
 *   edges shrink and fade slightly; a tapped card presses in. Scroll push is gentler, because
 *   thumb flicks are far faster than a mouse wheel.
 * - prefers-reduced-motion: tiles stay in a plain scrollable strip, nothing moves by itself.
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = matchMedia('(hover: hover) and (pointer: fine)');

const EASE_TO_STOP = 0.4;
const EASE_TO_RUN = 0.9;
const SLOW_FACTOR = 0.35;

const SCROLL_INFLUENCE = 0.45;
const SCROLL_INFLUENCE_PHONE = 0.22;
const SCROLL_EASE = 0.28;
const MAX_BOOST = 900;
const MAX_BOOST_PHONE = 380;
const FOCUS_MIN_SCALE = 0.92;
const FOCUS_MIN_OPACITY = 0.45;
const MAX_FLING = 2400;
const DRAG_THRESHOLD = 6;
const TILT_DEGREES = 5;

/**
 * @typedef {object} Lane
 * @property {HTMLElement} element
 * @property {HTMLElement} track
 * @property {number} direction
 * @property {number} speedFactor
 * @property {number} offset
 * @property {number} velocity
 * @property {number} target
 * @property {number} setWidth
 * @property {number} originals
 * @property {{ element: HTMLElement, left: number, width: number, scale: number, opacity: number }[]} tiles
 */

/**
 * @typedef {object} Drag
 * @property {Lane} lane
 * @property {number} pointerId
 * @property {number} startX
 * @property {number} lastX
 * @property {number} lastTime
 * @property {number} speed
 * @property {boolean} active
 */

class ReviewsShowcase extends HTMLElement {
  /** @type {Lane[]} */
  #lanes = [];
  /** @type {HTMLElement[]} */
  #tiles = [];
  #active = false;

  #frame = 0;
  #lastTime = 0;
  #inView = false;
  /** @type {Lane | null} */
  #hoveredLane = null;
  /** @type {Drag | null} */
  #drag = null;

  #lastScrollY = 0;
  #boost = 0;
  #focusMode = false;
  #scrollInfluence = SCROLL_INFLUENCE;
  #maxBoost = MAX_BOOST;
  /** @type {HTMLElement | null} */
  #pressedTile = null;
  /** @type {Animation | undefined} */
  #tickerAnimation;

  /** @type {IntersectionObserver | undefined} */
  #revealObserver;
  /** @type {IntersectionObserver | undefined} */
  #viewObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #resizeTimer = 0;
  #lastWidth = 0;

  get wall() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-wall]'));
  }

  get source() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.rs__source'));
  }

  get speed() {
    return Number(this.dataset.speed) || 48;
  }

  connectedCallback() {
    const wall = this.wall;
    if (!wall || !this.source) return;

    this.#tiles = /** @type {HTMLElement[]} */ ([...this.source.children]);
    this.#lastWidth = window.innerWidth;

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#assignRevealOrder();
        this.dataset.revealed = '';
        this.#revealObserver?.disconnect();
        this.#wake();
      },
      { threshold: 0.25 }
    );
    this.#revealObserver.observe(this);

    this.#viewObserver = new IntersectionObserver(
      ([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        this.#wake();
      },
      { rootMargin: '120px 0px' }
    );
    this.#viewObserver.observe(this);

    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(this);

    this.#setup();
    // Card sizes depend on the web font; re-measure the loop once it has loaded
    document.fonts?.ready.then(() => this.#remeasure());

    reducedMotion.addEventListener('change', this.#reset);
    document.addEventListener('visibilitychange', this.#wake);
    wall.addEventListener('pointermove', this.#onPointerMove);
    wall.addEventListener('pointerover', this.#onPointerOver);
    wall.addEventListener('pointerleave', this.#onPointerLeave);
    wall.addEventListener('pointerdown', this.#onPointerDown);
    wall.addEventListener('pointerup', this.#onPointerUp);
    wall.addEventListener('pointercancel', this.#onPointerUp);
    wall.addEventListener('lostpointercapture', this.#onPointerUp);
    wall.addEventListener('dragstart', this.#preventDefault);
  }

  disconnectedCallback() {
    this.#teardown();
    this.#revealObserver?.disconnect();
    this.#viewObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    reducedMotion.removeEventListener('change', this.#reset);
    document.removeEventListener('visibilitychange', this.#wake);
    const wall = this.wall;
    wall?.removeEventListener('pointermove', this.#onPointerMove);
    wall?.removeEventListener('pointerover', this.#onPointerOver);
    wall?.removeEventListener('pointerleave', this.#onPointerLeave);
    wall?.removeEventListener('pointerdown', this.#onPointerDown);
    wall?.removeEventListener('pointerup', this.#onPointerUp);
    wall?.removeEventListener('pointercancel', this.#onPointerUp);
    wall?.removeEventListener('lostpointercapture', this.#onPointerUp);
    wall?.removeEventListener('dragstart', this.#preventDefault);
  }

  /* ---------------------------------------------------------------- layout */

  #reset = () => {
    this.#teardown();
    this.#setup();
  };

  #setup() {
    const wall = this.wall;
    if (!wall || reducedMotion.matches || this.#tiles.length < 2) return;

    const phone = window.innerWidth < 750;
    const laneCount = !phone && this.#tiles.length >= 6 ? 2 : 1;
    wall.dataset.axis = 'x';
    this.#active = true;
    this.#focusMode = laneCount === 1;
    wall.toggleAttribute('data-focus', this.#focusMode);
    this.#scrollInfluence = phone ? SCROLL_INFLUENCE_PHONE : SCROLL_INFLUENCE;
    this.#maxBoost = phone ? MAX_BOOST_PHONE : MAX_BOOST;

    const container = document.createElement('div');
    container.className = 'rs__lanes';

    /** @type {HTMLElement[][]} */
    const buckets = Array.from({ length: laneCount }, () => []);
    this.#tiles.forEach((tile, index) => buckets[index % laneCount]?.push(tile));

    this.#lanes = buckets.map((tiles, index) => {
      const element = document.createElement('div');
      element.className = 'rs__lane';
      element.dataset.direction = index % 2 === 0 ? 'forward' : 'backward';
      const track = document.createElement('div');
      track.className = 'rs__lane-track';
      track.append(...tiles);
      element.append(track);
      container.append(element);
      return {
        element,
        track,
        direction: index % 2 === 0 ? 1 : -1,
        speedFactor: 1 + index * 0.12,
        offset: 0,
        velocity: 0,
        target: 0,
        setWidth: 0,
        originals: tiles.length,
        tiles: [],
      };
    });

    wall.append(container);
    for (const lane of this.#lanes) this.#fillLane(lane);

    this.#tickerAnimation = this.querySelector('.rs__ticker-track')?.getAnimations()[0];
    this.#lastScrollY = window.scrollY;
    if (this.hasAttribute('data-revealed')) this.#assignRevealOrder();
    this.#wake();
  }

  #teardown() {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    for (const tile of this.#tiles) {
      tile.style.removeProperty('--i');
      tile.style.transform = '';
      tile.style.opacity = '';
      this.source?.append(tile);
    }
    this.#releasePress();
    this.wall?.querySelector('.rs__lanes')?.remove();
    this.wall?.removeAttribute('data-axis');
    this.wall?.removeAttribute('data-focus');
    this.wall?.classList.remove('is-dragging');
    this.#lanes = [];
    this.#active = false;
    this.#hoveredLane = null;
    this.#drag = null;
    this.#boost = 0;
    this.#tickerAnimation?.updatePlaybackRate(1);
  }

  /** @param {Lane} lane */
  #fillLane(lane) {
    const originals = /** @type {HTMLElement[]} */ ([...lane.track.children]);
    const wallWidth = this.wall?.clientWidth || window.innerWidth;

    const appendCopy = () => {
      for (const item of originals) {
        const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
        clone.dataset.clone = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('role');
        clone.removeAttribute('data-shopify-editor-block');
        clone.inert = true;
        lane.track.append(clone);
      }
    };

    appendCopy();
    lane.setWidth = this.#measureSet(lane);

    // Enough copies that the row is always covered, even mid-wrap or mid-drag
    let guard = 0;
    while (lane.setWidth > 0 && lane.track.scrollWidth < wallWidth + lane.setWidth && guard++ < 8) {
      appendCopy();
    }

    this.#measureTiles(lane);

    if (this.#focusMode) {
      // Open with the first review centred and in focus
      const first = lane.tiles[0];
      const laneWidth = lane.element.clientWidth;
      lane.offset = first ? first.left + first.width / 2 - laneWidth / 2 : 0;
    } else {
      // Start the second row part-way through so the two rows never line up
      lane.offset = lane.direction === 1 ? 0 : lane.setWidth * 0.4;
    }
    this.#render(lane);
  }

  /**
   * Tile positions relative to the lane at offset 0 (offsetLeft ignores the track transform).
   * @param {Lane} lane
   */
  #measureTiles(lane) {
    lane.tiles = /** @type {HTMLElement[]} */ ([...lane.track.children]).map((element) => ({
      element,
      left: element.offsetLeft,
      width: element.offsetWidth,
      scale: -1,
      opacity: -1,
    }));
  }

  /** @param {Lane} lane */
  #measureSet(lane) {
    const first = lane.track.children[0];
    const firstClone = lane.track.children[lane.originals];
    if (!first || !firstClone) return 0;
    return firstClone.getBoundingClientRect().left - first.getBoundingClientRect().left;
  }

  #remeasure() {
    for (const lane of this.#lanes) {
      const previous = lane.setWidth;
      lane.setWidth = this.#measureSet(lane);
      if (previous > 0 && lane.setWidth > 0) lane.offset = (lane.offset / previous) * lane.setWidth;
      this.#measureTiles(lane);
      this.#render(lane);
    }
  }

  /** Only width matters: height changes (mobile address bar, lazy images) must not rebuild the rows. */
  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = window.setTimeout(() => {
      const width = window.innerWidth;
      if (width === this.#lastWidth) return;
      this.#lastWidth = width;
      this.#reset();
    }, 200);
  };

  /** Cards currently on screen cascade in from their row's side, left to right. */
  #assignRevealOrder() {
    const wallRect = this.wall?.getBoundingClientRect();
    if (!wallRect) return;
    for (const lane of this.#lanes) {
      let order = 0;
      for (const tile of /** @type {HTMLElement[]} */ ([...lane.track.children])) {
        const rect = tile.getBoundingClientRect();
        const visible = rect.right > wallRect.left && rect.left < wallRect.right;
        tile.style.setProperty('--i', String(visible ? order++ : 0));
      }
    }
  }

  /* ---------------------------------------------------------------- motion */

  get #running() {
    return this.#inView && this.hasAttribute('data-revealed') && !document.hidden;
  }

  #wake = () => {
    if (!this.#active) return;
    for (const lane of this.#lanes) lane.target = this.#targetFor(lane);
    if (this.#inView && !document.hidden && !this.#frame) {
      this.#lastTime = 0;
      this.#lastScrollY = window.scrollY;
      this.#frame = requestAnimationFrame(this.#tick);
    }
  };

  /** @param {Lane} lane */
  #targetFor(lane) {
    if (!this.#running || this.#hoveredLane === lane || this.#drag?.lane === lane) return 0;
    const base = this.speed * lane.speedFactor;
    return this.#hoveredLane ? base * SLOW_FACTOR : base;
  }

  /** @param {number} time */
  #tick = (time) => {
    const dt = this.#lastTime ? Math.min(time - this.#lastTime, 64) / 1000 : 0;
    this.#lastTime = time;

    // Page scroll velocity (px/s) becomes a boost that decays smoothly
    const scrollY = window.scrollY;
    const scrollVelocity = dt > 0 ? (scrollY - this.#lastScrollY) / dt : 0;
    this.#lastScrollY = scrollY;
    const boostTarget = this.hasAttribute('data-revealed') ? scrollVelocity * this.#scrollInfluence : 0;
    if (dt > 0) this.#boost += (boostTarget - this.#boost) * (1 - Math.exp(-dt / SCROLL_EASE));
    this.#boost = Math.max(-this.#maxBoost, Math.min(this.#maxBoost, this.#boost));
    if (Math.abs(this.#boost) < 0.5) this.#boost = 0;

    for (const lane of this.#lanes) {
      const dragging = this.#drag?.active && this.#drag.lane === lane;
      if (!dragging) {
        const slowing = Math.abs(lane.target) < Math.abs(lane.velocity);
        const tau = slowing ? EASE_TO_STOP : EASE_TO_RUN;
        lane.velocity += (lane.target - lane.velocity) * (1 - Math.exp(-dt / tau));
        if (lane.target === 0 && Math.abs(lane.velocity) < 0.05) lane.velocity = 0;

        const boost = this.#hoveredLane === lane ? 0 : this.#boost;
        lane.offset += (lane.velocity + boost) * lane.direction * dt;
      }

      if (lane.setWidth > 0) {
        lane.offset = ((lane.offset % lane.setWidth) + lane.setWidth) % lane.setWidth;
      }
      this.#render(lane);
    }

    if (this.#tickerAnimation) {
      const rate = 1 + this.#boost / 90;
      this.#tickerAnimation.updatePlaybackRate(Math.max(-5, Math.min(7, rate)));
    }

    if (!this.#inView || document.hidden) {
      this.#frame = 0;
      return;
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };

  /** @param {Lane} lane */
  #render(lane) {
    lane.track.style.transform = `translate3d(${-lane.offset.toFixed(2)}px, 0, 0)`;
    if (this.#focusMode) this.#renderFocus(lane);
  }

  /** @param {Lane} lane */
  #renderFocus(lane) {
    const laneWidth = lane.element.clientWidth;
    if (!laneWidth) return;
    const center = laneWidth / 2;

    for (const tile of lane.tiles) {
      // The offset stays within one set and the clones extend to the right, so this covers the screen
      const x = tile.left - lane.offset;
      if (x + tile.width < -tile.width || x > laneWidth + tile.width) continue;

      const distance = Math.min(Math.abs(x + tile.width / 2 - center) / (center + tile.width / 2), 1);
      // Flat around the centre, falling off towards the edges
      const falloff = distance * distance * (3 - 2 * distance);
      const scale = Math.round((1 - (1 - FOCUS_MIN_SCALE) * falloff) * 1000) / 1000;
      const opacity = Math.round((1 - (1 - FOCUS_MIN_OPACITY) * falloff) * 100) / 100;

      if (scale !== tile.scale) {
        tile.element.style.transform = `scale(${scale})`;
        tile.scale = scale;
      }
      if (opacity !== tile.opacity) {
        tile.element.style.opacity = String(opacity);
        tile.opacity = opacity;
      }
    }
  }

  /** @param {EventTarget | null} target */
  #press(target) {
    this.#releasePress();
    const tile = target instanceof Element ? target.closest('.rs-tile') : null;
    if (!(tile instanceof HTMLElement)) return;
    tile.classList.add('is-pressed');
    this.#pressedTile = tile;
  }

  #releasePress() {
    this.#pressedTile?.classList.remove('is-pressed');
    this.#pressedTile = null;
  }

  /* ---------------------------------------------------------------- pointer */

  /** @param {EventTarget | null} target */
  #laneFrom(target) {
    const element = target instanceof Element ? target.closest('.rs__lane') : null;
    return this.#lanes.find((lane) => lane.element === element) ?? null;
  }

  /** @param {Event} event */
  #preventDefault = (event) => event.preventDefault();

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !finePointer.matches) return;
    const lane = this.#laneFrom(event.target);
    if (lane === this.#hoveredLane) return;
    this.#hoveredLane = lane;
    this.#wake();
  };

  #onPointerLeave = () => {
    if (!this.#hoveredLane) return;
    this.#hoveredLane = null;
    this.#wake();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.#active || event.button !== 0) return;
    const lane = this.#laneFrom(event.target);
    if (!lane) return;
    this.#drag = {
      lane,
      pointerId: event.pointerId,
      startX: event.clientX,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      speed: 0,
      active: false,
    };
    if (event.pointerType !== 'mouse') this.#press(event.target);
    this.#wake();
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (drag && drag.pointerId === event.pointerId) {
      if (!drag.active && Math.abs(event.clientX - drag.startX) > DRAG_THRESHOLD) {
        drag.active = true;
        drag.lane.velocity = 0;
        this.#releasePress();
        drag.lane.element.setPointerCapture(event.pointerId);
        this.wall?.classList.add('is-dragging');
      }
      if (drag.active) {
        const dx = event.clientX - drag.lastX;
        const dt = Math.max(event.timeStamp - drag.lastTime, 1) / 1000;
        drag.lane.offset -= dx;
        // Smoothed pointer speed, used for the fling on release
        drag.speed = drag.speed * 0.6 + (dx / dt) * 0.4;
        drag.lastX = event.clientX;
        drag.lastTime = event.timeStamp;
        return;
      }
    }

    // Tilt + light follow the pointer on the hovered card
    if (event.pointerType !== 'mouse') return;
    const tile = event.target instanceof Element ? event.target.closest('.rs-tile') : null;
    if (!(tile instanceof HTMLElement)) return;
    const rect = tile.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    tile.style.setProperty('--mx', `${x}px`);
    tile.style.setProperty('--my', `${y}px`);
    tile.style.setProperty('--ry', `${((x / rect.width - 0.5) * TILT_DEGREES).toFixed(2)}deg`);
    tile.style.setProperty('--rx', `${((0.5 - y / rect.height) * TILT_DEGREES).toFixed(2)}deg`);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const drag = this.#drag;
    this.#releasePress();
    if (!drag || drag.pointerId !== event.pointerId) return;
    this.#drag = null;
    this.wall?.classList.remove('is-dragging');

    if (drag.active) {
      // Ignore a stale speed if the finger/mouse paused before letting go
      const idle = event.timeStamp - drag.lastTime > 90;
      const fling = idle ? 0 : Math.max(-MAX_FLING, Math.min(MAX_FLING, drag.speed));
      // offset moves by velocity * direction; dragging right (positive dx) means offset decreases
      drag.lane.velocity = -fling / drag.lane.direction;
    }
    this.#wake();
  };
}

if (!customElements.get('reviews-showcase')) {
  customElements.define('reviews-showcase', ReviewsShowcase);
}
